import React, { useState, useEffect, useCallback } from 'react';
import { getChangeProposals, approveProposal, rejectProposal, bulkApproveProposals, bulkRejectProposals } from '../../api/questionnaire';
import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../hooks/usePermissions';
import { ConfirmationModal } from '../ui/Modal';

const SOURCE_BADGES = {
  meeting_transcript: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Meeting' },
  meeting_notes: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Notes' },
  email: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Email' },
  document: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Document' },
};

const PROPOSAL_TYPE_BADGES = {
  add: { bg: 'bg-green-100', text: 'text-green-700', label: 'ADD' },
  update: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'UPDATE' },
};

const ChangeProposalPanel = ({ leadId, isOpen, onClose, onRefresh, isCompleted }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const toast = useToast();
  const { can } = usePermissions();
  // Read-only roles (viewer, compliance_officer) can view pending changes but cannot
  // approve/reject them (backend requires questionnaires:edit).
  const canReviewChanges = can.editQuestionnaire();

  const totalSources = questions.reduce((sum, q) => sum + q.sources.length, 0);

  const fetchProposals = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await getChangeProposals(leadId);
      setQuestions(res.data?.questions || []);
    } catch {
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  }, [leadId, toast]);

  useEffect(() => {
    if (isOpen) fetchProposals();
  }, [isOpen, fetchProposals]);

  const handleApprove = async (proposalId) => {
    setActionLoading(proposalId);
    try {
      await approveProposal(leadId, proposalId);
      toast.success('Change approved');
      await fetchProposals();
      onRefresh?.();
    } catch {
      toast.error('Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (proposalId) => {
    setActionLoading(proposalId);
    try {
      await rejectProposal(leadId, proposalId);
      toast.success('Change rejected');
      await fetchProposals();
      onRefresh?.();
    } catch {
      toast.error('Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkApprove = async () => {
    setActionLoading('bulk');
    try {
      const ids = questions.map(q => q.sources[0]?.id).filter(Boolean);
      await bulkApproveProposals(leadId, ids);
      toast.success('All changes approved');
      await fetchProposals();
      onRefresh?.();
    } catch {
      toast.error('Bulk approve failed');
    } finally {
      setActionLoading(null);
      setConfirmModal(null);
    }
  };

  const handleBulkReject = async () => {
    setActionLoading('bulk');
    try {
      const ids = questions.flatMap(q => q.sources.map(s => s.id));
      await bulkRejectProposals(leadId, ids);
      toast.success('All changes rejected');
      await fetchProposals();
      onRefresh?.();
    } catch {
      toast.error('Bulk reject failed');
    } finally {
      setActionLoading(null);
      setConfirmModal(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">

        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Pending Changes</h2>
            <p className="text-xs text-gray-500">{questions.length} question{questions.length !== 1 ? 's' : ''} with pending changes</p>
          </div>
          <div className="flex items-center gap-2">
            {questions.length > 0 && canReviewChanges && (
              <>
                <button
                  onClick={() => setConfirmModal('approve')}
                  disabled={actionLoading === 'bulk' || isCompleted}
                  title={isCompleted ? 'Questionnaire is completed — approve is disabled' : undefined}
                  className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 transition"
                >
                  Approve All
                </button>
                <button
                  onClick={() => setConfirmModal('reject')}
                  disabled={actionLoading === 'bulk'}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition"
                >
                  Reject All
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-8 text-sm text-gray-500">Loading proposals...</div>
          ) : questions.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">No pending changes</div>
          ) : (
            <div className="space-y-5">
              {questions.map((question) => {
                const proposalTypeBadge = PROPOSAL_TYPE_BADGES[question.proposal_type] || PROPOSAL_TYPE_BADGES.update;
                const isAdd = question.proposal_type === 'add';
                const multiSource = question.sources.length > 1;

                return (
                  <div key={question.question_id} className="border border-gray-400 rounded-xl overflow-hidden shadow-sm">

                    {/* ── Question header ── */}
                    <div className="bg-gray-50 px-4 pt-3 pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Q{question.question_id}</span>
                        <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${proposalTypeBadge.bg} ${proposalTypeBadge.text}`}>
                          {proposalTypeBadge.label}
                        </span>
                        {question.section_title && (
                          <span className="text-[10px] text-gray-400">{question.section_title}</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-800 leading-snug">{question.question_text}</p>

                      {/* Current value shown once here */}
                      {!isAdd && (
                        <div className="mt-2 bg-red-50 border border-red-200 rounded p-2.5">
                          <span className="text-[9px] font-semibold text-red-500 uppercase tracking-wide">Current</span>
                          <p className="text-xs text-red-900 mt-1 whitespace-pre-wrap break-words">
                            {question.current_value || <span className="italic text-red-400">Empty</span>}
                          </p>
                        </div>
                      )}

                      {multiSource && (
                        <p className="text-[10px] text-amber-600 mt-2 italic">
                          Approving one source will reject all others for this question
                        </p>
                      )}
                    </div>

                    {/* ── Source rows (proposed values only) ── */}
                    <div className="divide-y divide-gray-100 bg-white">
                      {question.sources.map((source, idx) => {
                        const sourceBadge = SOURCE_BADGES[source.source_type] || SOURCE_BADGES.document;
                        const isLoading = actionLoading === source.id;
                        const sourceLabel = source.source_name
                          ? `${sourceBadge.label}: ${source.source_name}`
                          : sourceBadge.label;

                        return (
                          <div key={source.id} className="px-4 py-3">
                            {/* Row: source badge + confidence + approve/reject */}
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                {multiSource && (
                                  <span className="text-[10px] font-semibold text-gray-300">#{idx + 1}</span>
                                )}
                                <span
                                  className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${sourceBadge.bg} ${sourceBadge.text} truncate max-w-[220px]`}
                                  title={sourceLabel}
                                >
                                  {sourceLabel}
                                </span>
                                {source.confidence_score && (
                                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                    {Math.round(source.confidence_score * 100)}% confidence
                                  </span>
                                )}
                              </div>
                              {canReviewChanges && (
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <button
                                    onClick={() => handleApprove(source.id)}
                                    disabled={isLoading || isCompleted}
                                    title={isCompleted ? 'Questionnaire is completed — approve is disabled' : undefined}
                                    className="px-2.5 py-1 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50 transition"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleReject(source.id)}
                                    disabled={isLoading}
                                    className="px-2.5 py-1 text-[11px] font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50 transition"
                                  >
                                    Reject
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Proposed value */}
                            <div className="bg-green-50 border border-green-100 rounded p-2.5">
                              <span className="text-[9px] font-semibold text-green-600 uppercase tracking-wide">
                                {isAdd ? 'New Value' : 'Proposed Change'}
                              </span>
                              <p className="text-xs text-green-900 mt-1 whitespace-pre-wrap break-words">
                                {source.proposed_value}
                              </p>
                            </div>

                            {source.update_reason && (
                              <p className="text-[10px] text-gray-400 mt-1.5 italic">
                                {source.update_reason}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>

    <ConfirmationModal
      isOpen={confirmModal === 'approve'}
      onClose={() => setConfirmModal(null)}
      onConfirm={handleBulkApprove}
      title="Approve All Changes"
      message={`Are you sure you want to approve all ${questions.length} pending change${questions.length !== 1 ? 's' : ''}? The highest-confidence source will be applied for each question.`}
      confirmText="Approve All"
      confirmVariant="primary"
      isLoading={actionLoading === 'bulk'}
    />

    <ConfirmationModal
      isOpen={confirmModal === 'reject'}
      onClose={() => setConfirmModal(null)}
      onConfirm={handleBulkReject}
      title="Reject All Changes"
      message={`Are you sure you want to reject all ${totalSources} pending change${totalSources !== 1 ? 's' : ''}? This action cannot be undone.`}
      confirmText="Reject All"
      confirmVariant="outline"
      isLoading={actionLoading === 'bulk'}
    />
    </>
  );
};

export default ChangeProposalPanel;
